import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import moment, { Moment } from "moment";
import { AppDatePicker } from "component/shared/date-picker";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Dialog } from "component/shared/dialog";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Select } from "component/shared/select";
import { Table, Column } from "component/shared/table";
import { parseDateParam, parseIntParam } from "component/shared/report-filter-params";
import { TablePagination } from "component/shared/table/table-pagination";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getReportsLeadsActivities,
	getReportsLeadsTotalCount,
	getReportsLeadsTypes,
	getReportsLeadsLoading,
	getReportsLeadsLoaded,
} from "redux/selector";
import { fetchContactEventTypes, fetchLeadContactReport } from "redux/slice/reports-leads";
import {
	LeadContactActivityResource,
	ContactEventTypeResource,
	LeadContactReportQuery,
} from "api/resource/lead-contact-report";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Leads & Contacts";
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100];
// CF gates the listing block + Show Listing link and the message block on the contact-event type.
const LISTING_TYPES = [1, 2, 3];
const PROPERTY_ORGANIZER_SIGNUP = 4;
// value-0 "all" option, prepended client-side (StatsContact_output_1.cfm:113-118).
const ALL_TYPES: ContactEventTypeResource = { typeId: 0, name: "--- Get All Types ---" };

const formatDate = (iso: string | null): string => (iso ? moment(iso).format("MM/DD/YYYY") : "");
const formatTime = (iso: string | null): string => (iso ? moment(iso).format("hh:mm A") : "");
const formatDateTime = (iso: string | null): string =>
	iso ? moment(iso).format("MM/DD/YYYY hh:mm A") : "";

// mailto: body per CF rules (StatsContact_output_1.cfm:210-218): header line; listing number/address
// for types 1-3; preferred/alternate times for type 2; message for non-type-4 with single quotes
// stripped; newlines encoded as %0A (encodeURIComponent turns "\n" into %0A).
const buildMailtoHref = (activity: LeadContactActivityResource): string => {
	// CF prepends a leading blank line before the header (StatsContact_output_1.cfm:210-218).
	const lines: string[] = ["", `${activity.typeName} Details`];
	const listing = activity.listing;
	if (LISTING_TYPES.includes(activity.typeId) && listing) {
		lines.push(`Listing Number: ${listing.listingNumber || ""}`);
		if (listing.streetName) {
			lines.push(
				`Address: ${listing.streetNumber || ""} ${listing.streetName} ${listing.city || ""}, ${listing.state || ""} ${listing.zip || ""}`
			);
		}
	}
	if (activity.typeId === 2) {
		lines.push(`Preferred Time: ${formatDateTime(activity.preferableOn)}`);
		lines.push(`Alternate Time: ${formatDateTime(activity.alternativeOn)}`);
	}
	if (activity.typeId !== PROPERTY_ORGANIZER_SIGNUP && activity.message) {
		// Strip single quotes and normalize CRLF / lone CR to \n so stored carriage returns encode as
		// %0A (CF CHR(13)→%0A), not %0D. encodeURIComponent turns the "\n" into %0A.
		const cleaned = activity.message.replace(/\r\n?/g, "\n").replace(/'/g, "");
		lines.push(`Message: ${cleaned}`);
	}
	const subject = `Re: ${activity.typeName}`;
	const body = lines.join("\n");
	return `mailto:${activity.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.reporting}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.BarChart2 />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/StatsContact.cfm" />}
	/>
);

const ReportsLeadsBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const activities = useSelector((state: RootState) => getReportsLeadsActivities(state));
	const totalCount = useSelector((state: RootState) => getReportsLeadsTotalCount(state));
	const types = useSelector((state: RootState) => getReportsLeadsTypes(state));
	const loading = useSelector((state: RootState) => getReportsLeadsLoading(state));
	const loaded = useSelector((state: RootState) => getReportsLeadsLoaded(state));

	// Draft filter state (applied to the query on Submit / paging). Default range today-3 → today.
	// Initialized from URL params so filters persist across back-nav / refresh / share (MAX-3930).
	const [typeId, setTypeId] = useState(() => parseIntParam(location.search, "typeId") ?? 0);
	const [startDate, setStartDate] = useState<Moment>(() => parseDateParam(location.search, "startDate") ?? moment().subtract(3, "days"));
	const [endDate, setEndDate] = useState<Moment>(() => parseDateParam(location.search, "endDate") ?? moment());
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

	const [dialogActivity, setDialogActivity] = useState<LeadContactActivityResource | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success",
	});

	const typeOptions = useMemo(() => [ALL_TYPES, ...types], [types]);

	const loadReport = React.useCallback(
		async (nextPage: number, nextSize: number) => {
			const query: LeadContactReportQuery = {
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
				typeId: typeId > 0 ? typeId : undefined,
				offset: nextPage * nextSize,
				limit: nextSize,
				sortDir: "DESC",
			};
			try {
				// F4: .unwrap() so an API failure rejects here instead of silently resolving.
				await dispatch(fetchLeadContactReport(query) as any).unwrap();
			} catch{
				setSnackbar({ open: true, message: "Failed to load report", severity: "error" });
			}
		},
		[dispatch, startDate, endDate, typeId]
	);

	// Initial load: type dropdown + the default-range report (CF renders results on load).
	useEffect(() => {
		dispatch(fetchContactEventTypes() as any);
		loadReport(0, DEFAULT_PAGE_SIZE);
	}, [dispatch]);

	// Success banner if arriving with a ?Message= param (StatsContact_output_1.cfm:82-91).
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("Message");
		if (message) {
			setSnackbar({ open: true, message, severity: "success" });
		}
	}, [location.search]);

	const handleSubmit = () => {
		// Reflect the applied filters in the URL so they persist across back-nav / refresh / share
		// (spec-directed URL query params in place of CF's per-client session vars).
		const params = new URLSearchParams({
			startDate: startDate.format("YYYY-MM-DD"),
			endDate: endDate.format("YYYY-MM-DD"),
		});
		if (typeId > 0) {
			params.set("typeId", String(typeId));
		}
		history.replace({ search: params.toString() });
		setPage(0);
		loadReport(0, pageSize);
	};

	const handlePageChange = (nextPage: number) => {
		setPage(nextPage);
		loadReport(nextPage, pageSize);
	};

	const handlePageSizeChange = (nextSize: number) => {
		setPageSize(nextSize);
		setPage(0);
		loadReport(0, nextSize);
	};

	const renderDetails = (activity: LeadContactActivityResource) => {
		const listing = activity.listing;
		// Inline listing block renders for types 1-3 (CF shows number/address even with no URL); the
		// Show Listing button additionally requires a detailUrl so we never window.open(undefined).
		const showListingBlock = LISTING_TYPES.includes(activity.typeId) && !!listing;
		const showListingButton = showListingBlock && !!listing!.detailUrl;
		const message = activity.message || "";
		const truncated = message.length > 200;
		const preview = truncated ? message.substring(0, 200) : message;
		return (
			<Mui.Grid container direction="column" spacing={1}>
				<Mui.Grid item className={classes.actions}>
					<Mui.Button
						size="small"
						variant="outlined"
						href={buildMailtoHref(activity)}
						data-testid={`report-email-reply-${activity.contactEventId}`}
					>
						Email Reply
					</Mui.Button>
					<Mui.Button
						size="small"
						variant="outlined"
						component={RouterLinkWrapper}
						to={`/lead/${activity.leadId}`}
						data-testid={`report-manage-account-${activity.contactEventId}`}
					>
						Manage Account
					</Mui.Button>
					{showListingButton && (
						<Mui.Tooltip title="View listing on your website">
							<Mui.IconButton
								size="small"
								aria-label="View listing on your website"
								onClick={() => window.open(listing!.detailUrl, "_blank")}
								data-testid={`report-show-listing-${activity.contactEventId}`}
							>
								<FeatherIcon fontSize="small">
									<Icons.ExternalLink />
								</FeatherIcon>
							</Mui.IconButton>
						</Mui.Tooltip>
					)}
				</Mui.Grid>

				{showListingBlock && (
					<Mui.Grid item>
						<Mui.Typography variant="body2">
							<span className={classes.detailLabel}>Listing Number: </span>
							{listing!.listingNumber}
						</Mui.Typography>
						{listing!.streetName && (
							<Mui.Typography variant="body2">
								<span className={classes.detailLabel}>Address: </span>
								{`${listing!.streetNumber || ""} ${listing!.streetName} ${listing!.city || ""}, ${listing!.state || ""} ${listing!.zip || ""}`}
							</Mui.Typography>
						)}
					</Mui.Grid>
				)}

				{activity.typeId === 2 && (
					<Mui.Grid item>
						<Mui.Typography variant="body2">
							<span className={classes.detailLabel}>Preferred Time: </span>
							{formatDateTime(activity.preferableOn)}
						</Mui.Typography>
						<Mui.Typography variant="body2">
							<span className={classes.detailLabel}>Alternate Time: </span>
							{formatDateTime(activity.alternativeOn)}
						</Mui.Typography>
					</Mui.Grid>
				)}

				{activity.typeId !== PROPERTY_ORGANIZER_SIGNUP && (
					<Mui.Grid item>
						{message ? (
							<Mui.Typography variant="body2" className={classes.message}>
								<span className={classes.detailLabel}>Message: </span>
								{preview}
								{truncated && (
									<>
										{"... "}
										<Mui.Link
											component="button"
											type="button"
											onClick={() => setDialogActivity(activity)}
											data-testid={`report-read-full-${activity.contactEventId}`}
										>
											Read Full Message
										</Mui.Link>
									</>
								)}
							</Mui.Typography>
						) : (
							<Mui.Typography variant="body2" color="textSecondary">
								No message
							</Mui.Typography>
						)}
					</Mui.Grid>
				)}
			</Mui.Grid>
		);
	};

	const columns: Column<LeadContactActivityResource>[] = [
		{
			id: "type",
			title: "Type",
			value: (activity) => (
				<span data-testid={`report-row-${activity.contactEventId}`}>{activity.typeName}</span>
			),
		},
		{
			id: "nameEmail",
			title: "Name/Email",
			value: (activity) => (
				<>
					<Mui.Typography variant="body2">{activity.name}</Mui.Typography>
					<Mui.Typography variant="body2" color="textSecondary">
						{activity.email}
					</Mui.Typography>
				</>
			),
		},
		{
			id: "dateTime",
			title: "Date/Time",
			value: (activity) => (
				<>
					<Mui.Typography variant="body2">{formatDate(activity.createdOn)}</Mui.Typography>
					<Mui.Typography variant="body2" color="textSecondary">
						{formatTime(activity.createdOn)}
					</Mui.Typography>
				</>
			),
		},
		{
			id: "details",
			title: "Details",
			value: (activity) => renderDetails(activity),
		},
	];

	const renderResults = () => {
		if (loaded && activities.length === 0) {
			return (
				<Mui.Typography variant="body2" color="textSecondary">
					No results found.
				</Mui.Typography>
			);
		}
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">Lead &amp; Contact Results ({totalCount})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					{/* Server-paged: the shared Table hardcodes count=items.length and always slices to
						pageSize, so we render only the current page (hidePagination + pageSize=row count,
						MAX-3876 F8) and drive real paging with a separate TablePagination bound to
						totalCount below. */}
					<Table
						items={activities}
						columns={columns}
						hidePagination
						pageSize={Math.max(activities.length, 1)}
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<TablePagination
						count={totalCount}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={PAGE_SIZE_OPTIONS}
						onPageChange={(_event, nextPage) => handlePageChange(nextPage)}
						onRowsPerPageChange={(event) => handlePageSizeChange(parseInt(event.target.value, 10))}
					/>
				</Mui.Grid>
			</Mui.Grid>
		);
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.reporting}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.BarChart2 />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				<Mui.Grid container spacing={2} alignItems="flex-end" className={classes.filterBar}>
					<Mui.Grid item xs={12} sm={3} data-testid="report-filter-type">
						<Select
							variant="outlined"
							label="Type"
							value={typeId}
							options={typeOptions}
							valueExtractor={(option: ContactEventTypeResource) => option.typeId}
							labelExtractor={(option: ContactEventTypeResource) => option.name}
							optionRenderer={(option: ContactEventTypeResource) => (
								<Mui.ListItemText primary={option.name} />
							)}
							onChange={(value) => {
								setTypeId(value == null || value === "" ? 0 : Number(value));
								setPage(0);
							}}
						/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="report-filter-start">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="Start Date"
								placeholder="mm/dd/yyyy"
								value={startDate.toDate()}
								onChange={(date) => {
									setStartDate(date ? moment(date) : moment());
									setPage(0);
								}}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="report-filter-end">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="End Date"
								placeholder="mm/dd/yyyy"
								value={endDate.toDate()}
								onChange={(date) => {
									setEndDate(date ? moment(date) : moment());
									setPage(0);
								}}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							data-testid="report-filter-submit"
						>
							Submit
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>

				{loading && !loaded ? <Mui.CircularProgress /> : renderResults()}
			</FullPageCard>

			<Dialog open={!!dialogActivity} onClose={() => setDialogActivity(null)}>
				<Mui.DialogTitle>Full Message</Mui.DialogTitle>
				<Mui.DialogContent>
					{dialogActivity && (
						<>
							<Mui.Typography variant="body2">{dialogActivity.name}</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary">
								{dialogActivity.email}
							</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary">
								{formatDateTime(dialogActivity.createdOn)}
							</Mui.Typography>
							<Mui.Typography variant="body2" className={classes.message}>
								{dialogActivity.message}
							</Mui.Typography>
						</>
					)}
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button onClick={() => setDialogActivity(null)} variant="contained" color="primary">
						Close
					</Mui.Button>
				</Mui.DialogActions>
			</Dialog>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity={snackbar.severity}>
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ReportsLeads = withStyles(ReportsLeadsBase, styles);

const ReportsLeadsPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ReportsLeads {...props} />;
};

export const ReportsLeadsPage = withStyles(ReportsLeadsPageBase, styles);
